import { FC, ReactNode } from 'react';

import { useBreakpoint, useFallbackTranslation } from '../../utils';
import { useCookiebotConsent } from '../../utils/hooks/useCookiebotConsent';
import { Button } from '../Button/Button';

export type CookiebotVideoGateProps = {
  youtubeId: string;
  isYouku?: boolean;
  className?: string;
  children: ReactNode;
};

/**
 * Gates a marketing video (YouTube) behind Cookiebot marketing consent. When
 * consent is missing it renders a friendly placeholder with a way to reopen the
 * consent dialog and a direct link to the video; otherwise it renders `children`
 * (the consumer's iframe).
 */
export const CookiebotVideoGate: FC<CookiebotVideoGateProps> = ({
  youtubeId,
  isYouku,
  className,
  children,
}) => {
  const { t } = useFallbackTranslation();
  const { hasMarketingConsent, openCookieSettings } = useCookiebotConsent();
  const { isAboveMd } = useBreakpoint('md', true);

  if (isYouku || hasMarketingConsent) return <>{children}</>;

  const buttonSize = isAboveMd ? 'm' : 's';

  return (
    <div className={`dark flex flex-col items-center justify-center gap-5 p-2 ${className || ''}`}>
      <p className="text-center text-black md:text-lg dark:text-white">
        {t('system.AcceptMarketingCookiesToPlayVideo')}
      </p>
      <div className="flex flex-wrap items-center justify-center gap-5">
        <Button variant="primary" size={buttonSize} onClick={openCookieSettings}>
          {t('system.ChangeCookieSettings')}
        </Button>
        <Button
          variant="secondary"
          size={buttonSize}
          asLink
          href={`https://www.youtube.com/watch?v=${youtubeId}`}
          rel="noopener noreferrer"
        >
          {t('system.WatchVideoOnYouTube')}
        </Button>
      </div>
    </div>
  );
};
